<template>
    <div class="animated fadeIn booking-view-page">
        <component-header
            :title="`${booking.bookingId} – ${booking.type}`"
            :hasSearch="false"
            :hasCreate="false"
            :hasReload="false"
            :hasback="true"
            @back-press="goBack"
        >
        </component-header>

        <b-card class="card-wrap booking-view-card mt-3" border-variant="info">
            <div class="row">
                <!-- LEFT COLUMN -->
                <div class="col-12 col-lg-8">
                    <!-- ROUTE & LOCATION -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-map-marker mr-2"></i>
                                <span>Route &amp; Location</span>
                            </div>
                        </template>
                        <div class="map-card">
                            <div class="map-chip">{{ booking.city }}</div>

                            <div class="map-canvas">
                                <div class="route-line"></div>
                                <div class="marker marker-pickup"></div>
                                <div class="marker marker-dropoff"></div>

                                <div class="place-tag place-pickup">
                                    <div class="place-label">PICKUP</div>
                                    <div class="place-name">
                                        {{ booking.route.pickup.area }}
                                    </div>
                                </div>

                                <div class="place-tag place-dropoff">
                                    <div class="place-label">DROPOFF</div>
                                    <div class="place-name">
                                        {{ booking.route.dropoff.area }}
                                    </div>
                                </div>

                                <div class="distance-pill">
                                    <i
                                        class="fa fa-road mr-2"
                                        aria-hidden="true"
                                    ></i>
                                    {{ booking.route.distance }} &ndash;
                                    {{ booking.route.duration }}
                                </div>
                            </div>

                            <div class="route-meta">
                                <div class="route-item">
                                    <div class="dot dot-green"></div>
                                    <div>
                                        <div class="route-item-title">
                                            Pickup Location
                                        </div>
                                        <div class="route-item-sub">
                                            {{ booking.route.pickup.full }}
                                        </div>
                                    </div>
                                </div>

                                <div class="route-item">
                                    <div class="dot dot-red"></div>
                                    <div>
                                        <div class="route-item-title">
                                            Dropoff Location
                                        </div>
                                        <div class="route-item-sub">
                                            {{ booking.route.dropoff.full }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </b-card>

                    <!-- TRIP TIMELINE -->
                    <b-card class="card-wrap section-card">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-clock-o mr-2"></i>
                                <span>Trip Timeline</span>
                            </div>
                        </template>
                        <div class="timeline">
                            <div
                                class="timeline-row"
                                v-for="(t, idx) in booking.timeline"
                                :key="idx"
                            >
                                <div class="timeline-left">
                                    <div
                                        class="timeline-dot"
                                        :class="
                                            t.completed ? 'done' : 'pending'
                                        "
                                    ></div>
                                    <div
                                        class="timeline-line"
                                        v-if="
                                            idx !== booking.timeline.length - 1
                                        "
                                        :class="timelineLineClass(idx)"
                                    ></div>
                                </div>

                                <div class="timeline-content">
                                    <div class="timeline-title">
                                        {{ t.title }}
                                    </div>
                                    <div class="timeline-sub">
                                        {{ t.subtitle }}
                                    </div>
                                </div>

                                <div class="timeline-time">{{ t.time }}</div>
                            </div>
                        </div>
                    </b-card>
                </div>

                <!-- RIGHT COLUMN -->
                <div class="col-12 col-lg-4 mt-3 mt-lg-0">
                    <!-- CUSTOMER DETAILS -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-user mr-2"></i>
                                <span>Customer Details</span>
                            </div>
                        </template>
                        <div class="person-row">
                            <div class="avatar blue">
                                <i class="fa fa-user" aria-hidden="true"></i>
                            </div>
                            <div class="person-info">
                                <div class="person-name">
                                    {{ booking.customer.name }}
                                </div>
                                <div class="person-tag">
                                    {{ booking.customer.tag }}
                                </div>
                            </div>
                        </div>

                        <div class="person-phone">
                            <i class="fa fa-phone mr-2" aria-hidden="true"></i>
                            {{ booking.customer.phone }}
                        </div>

                        <div class="stats-grid">
                            <div class="stat-item">
                                <div class="stat-label">Total Rides</div>
                                <div class="stat-value">
                                    {{ booking.customer.totalRides }}
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-label">Rating</div>
                                <div class="stat-value rating">
                                    <i
                                        class="fa fa-star star"
                                        aria-hidden="true"
                                    ></i>
                                    {{ booking.customer.rating }}
                                </div>
                            </div>
                        </div>
                    </b-card>

                    <!-- DRIVER DETAILS -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-id-card mr-2"></i>
                                <span>Driver Details</span>
                            </div>
                        </template>
                        <div class="person-row">
                            <div class="avatar green">
                                <i class="fa fa-user" aria-hidden="true"></i>
                            </div>
                            <div class="person-info">
                                <div class="person-name">
                                    {{ booking.driver.name }}
                                </div>
                                <div class="person-tag">
                                    {{ booking.driver.tag }}
                                </div>
                            </div>
                        </div>

                        <div class="person-phone">
                            <i class="fa fa-phone mr-2" aria-hidden="true"></i>
                            {{ booking.driver.phone }}
                        </div>

                        <div class="stats-grid">
                            <div class="stat-item">
                                <div class="stat-label">Total Trips</div>
                                <div class="stat-value">
                                    {{ booking.driver.totalTrips }}
                                </div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-label">Rating</div>
                                <div class="stat-value rating">
                                    <i
                                        class="fa fa-star star"
                                        aria-hidden="true"
                                    ></i>
                                    {{ booking.driver.rating }}
                                </div>
                            </div>
                        </div>

                        <div class="driver-status-row">
                            <div class="stat-label">Status</div>
                            <span class="mini-pill status-active">active</span>
                        </div>
                    </b-card>

                    <!-- VEHICLE DETAILS -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-car mr-2"></i>
                                <span>Vehicle Details</span>
                            </div>
                        </template>
                        <div class="vehicle-row">
                            <div class="avatar purple">
                                <i class="fa fa-car" aria-hidden="true"></i>
                            </div>
                            <div>
                                <div class="vehicle-name">
                                    {{ booking.vehicle.name }}
                                </div>
                                <div class="vehicle-plate">
                                    {{ booking.vehicle.plate }}
                                </div>
                            </div>
                        </div>
                    </b-card>

                    <!-- PAYMENT DETAILS -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-credit-card mr-2"></i>
                                <span>Payment Details</span>
                            </div>
                        </template>
                        <div class="kv-row">
                            <div class="kv-key">Payment Method</div>
                            <div class="kv-val">
                                {{ booking.payment.method }}
                            </div>
                        </div>

                        <div class="divider"></div>

                        <div
                            class="kv-row"
                            v-for="(p, idx) in booking.payment.breakup"
                            :key="idx"
                        >
                            <div class="kv-key">{{ p.label }}</div>
                            <div class="kv-val">
                                {{ formatAmount(p.amount) }}
                            </div>
                        </div>

                        <div class="divider"></div>

                        <div class="kv-row total">
                            <div class="kv-key">Total Amount</div>
                            <div class="kv-val total-amount">
                                {{ formatAmount(booking.amount) }}
                            </div>
                        </div>

                        <div class="paid-pill-wrap">
                            <span class="mini-pill paid">Paid</span>
                        </div>
                    </b-card>

                    <!-- BOOKING INFORMATION -->
                    <b-card class="card-wrap section-card mb-3">
                        <template #header>
                            <div class="section-header">
                                <i class="fa fa-info-circle mr-2"></i>
                                <span>Booking Information</span>
                            </div>
                        </template>
                        <div class="kv-row">
                            <div class="kv-key">Booking ID</div>
                            <div class="kv-val">{{ booking.bookingId }}</div>
                        </div>
                        <div class="kv-row">
                            <div class="kv-key">City</div>
                            <div class="kv-val">{{ booking.city }}</div>
                        </div>
                        <div class="kv-row">
                            <div class="kv-key">Date</div>
                            <div class="kv-val">{{ booking.date }}</div>
                        </div>
                        <div class="kv-row">
                            <div class="kv-key">Time</div>
                            <div class="kv-val">{{ booking.time }}</div>
                        </div>
                    </b-card>

                    <!-- ACTION BUTTONS -->
                    <div class="actions-wrap mt-3">
                        <b-button
                            variant="primary"
                            size="sm"
                            class="action-btn w-100 mb-2"
                        >
                            <i class="fa fa-phone mr-1"></i> Contact Customer
                        </b-button>
                        <b-button
                            variant="success"
                            size="sm"
                            class="action-btn w-100 mb-2"
                        >
                            <i class="fa fa-phone mr-1"></i> Contact Driver
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="action-btn w-100 mb-2"
                        >
                            Reassign Driver
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="action-btn w-100"
                        >
                            Apply Refund
                        </b-button>
                    </div>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";

export default {
    name: "BookingView",
    components: { ComponentHeader },
    computed: {
        bookingId() {
            return (
                (this.$route && this.$route.params && this.$route.params.id) ||
                "RB-3847"
            );
        },
        booking() {
            const base = {
                bookingId: "RB-3847",
                type: "Economy - Mini",
                status: "completed",
                city: "Bangalore",
                amount: 245,
                date: "6 Jan 2026",
                time: "2:30 PM",
                route: {
                    distance: "8.2 km",
                    duration: "22 mins",
                    pickup: {
                        area: "Indiranagar",
                        full: "Indiranagar, Bangalore",
                    },
                    dropoff: {
                        area: "Jayanagar",
                        full: "Jayanagar, Bangalore",
                    },
                },
                customer: {
                    name: "Rajesh Kumar",
                    tag: "Regular Customer",
                    phone: "+91 98765 43210",
                    totalRides: 47,
                    rating: 4.9,
                },
                driver: {
                    name: "Amit Sharma",
                    tag: "Professional Driver",
                    phone: "+91 97654 32109",
                    totalTrips: 1234,
                    rating: 4.8,
                },
                vehicle: {
                    name: "Maruti Swift",
                    plate: "KA-01-AB-1231",
                },
                payment: {
                    method: "UPI",
                    breakup: [
                        { label: "Base Fare", amount: 60 },
                        { label: "Distance Charge", amount: 110 },
                        { label: "Time Charge", amount: 70 },
                        { label: "Platform Fee", amount: 10 },
                        { label: "GST (10%)", amount: 25 },
                    ],
                },
                timeline: [
                    {
                        title: "Booking Created",
                        subtitle: "Customer initiated the booking request",
                        time: "2:15 PM",
                        completed: true,
                    },
                    {
                        title: "Driver Assigned",
                        subtitle: "Amit Sharma accepted the booking",
                        time: "2:16 PM",
                        completed: true,
                    },
                    {
                        title: "Driver Arrived at Pickup",
                        subtitle: "Driver reached pickup location",
                        time: "2:20 PM",
                        completed: true,
                    },
                    {
                        title: "Trip Started",
                        subtitle: "Journey commenced",
                        time: "2:22 PM",
                        completed: true,
                    },
                    {
                        title: "Trip Completed",
                        subtitle: "Successfully completed",
                        time: "2:30 PM",
                        completed: true,
                    },
                ],
            };

            // Keep design static; only swap bookingId if route param differs
            return {
                ...base,
                bookingId: this.bookingId || base.bookingId,
            };
        },
    },
    methods: {
        goBack() {
            this.$router.push("/bookings");
        },
        timelineLineClass(idx) {
            const list = (this.booking && this.booking.timeline) || [];
            const curr = list[idx];
            const next = list[idx + 1];
            const done = Boolean(
                curr && curr.completed && next && next.completed,
            );
            return done ? "line-done" : "line-pending";
        },
        normalizeStatus(status) {
            return String(status || "")
                .trim()
                .toLowerCase()
                .replace(/\s+/g, "-");
        },
        statusLabel(status) {
            const s = this.normalizeStatus(status);
            if (s === "in-progress") return "in-progress";
            if (s === "completed") return "completed";
            if (s === "cancelled") return "cancelled";
            return status || "";
        },
        statusClass(status) {
            const s = this.normalizeStatus(status);
            const classMap = {
                completed: "badge-success",
                "in-progress": "badge-info",
                cancelled: "badge-danger",
            };
            return classMap[s] || "badge-secondary";
        },
        formatAmount(amount) {
            const num = Number(amount);
            if (Number.isNaN(num)) return "₹0";
            try {
                return new Intl.NumberFormat("en-IN", {
                    style: "currency",
                    currency: "INR",
                    maximumFractionDigits: 0,
                }).format(num);
            } catch (e) {
                return `₹${num}`;
            }
        },
    },
};
</script>

<style scoped>
.booking-view-page {
    padding: 8px 0 24px;
}

.card-wrap {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.06);
    border-radius: 12px;
}

.booking-view-card {
    padding: 0.25rem;
}

.section-card {
    border: 1px solid var(--slate-200, #e2e8f0);
}

.section-card ::v-deep .card-header {
    background: var(--slate-50, #f8fafc);
    border-bottom: 1px solid var(--slate-200, #e2e8f0);
    padding: 0.75rem 1rem;
    font-weight: 600;
    font-size: 14px;
    color: var(--slate-800, #1e293b);
}

.section-header {
    display: flex;
    align-items: center;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.status-badge.badge-success {
    background: var(--success-bg);
    color: var(--success-text);
}

.status-badge.badge-info {
    background: var(--info-bg);
    color: var(--info-text);
}

.status-badge.badge-danger {
    background: var(--danger-bg);
    color: var(--danger-text);
}

.status-badge.badge-secondary {
    background: var(--slate-100);
    color: var(--slate-700);
}

.action-btn {
    border-radius: 10px !important;
    font-weight: 600 !important;
    font-size: 13px !important;
}

/* MAP */
.map-card {
    position: relative;
    border-radius: 14px;
    border: 1px solid var(--slate-200);
    overflow: hidden;
}

.map-canvas {
    position: relative;
    height: 220px;
    background:
        linear-gradient(#eef2f7 1px, transparent 1px),
        linear-gradient(90deg, #eef2f7 1px, transparent 1px);
    background-size: 24px 24px;
    background-color: #f8fafc;
}

.map-chip {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid var(--slate-200);
    background: #fff;
    font-size: 12px;
    font-weight: 700;
    color: var(--slate-700);
}

.route-line {
    position: absolute;
    left: 56px;
    top: 34px;
    bottom: 34px;
    width: 2px;
    z-index: 1;
    background: repeating-linear-gradient(
        to bottom,
        rgba(37, 99, 235, 0.65),
        rgba(37, 99, 235, 0.65) 6px,
        transparent 6px,
        transparent 12px
    );
}

.marker {
    position: absolute;
    left: 48px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: var(--shadow-sm);
    z-index: 2;
}

.marker-pickup {
    top: 28px;
    background: var(--success);
}

.marker-dropoff {
    bottom: 28px;
    background: var(--danger);
}

.distance-pill {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 4;
    background: var(--primary, #2563eb);
    color: #fff;
    font-weight: 700;
    font-size: 12px;
    border-radius: 999px;
    padding: 6px 12px;
    box-shadow: var(--shadow-md);
    white-space: nowrap;
}

.place-tag {
    position: absolute;
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 10px;
    padding: 8px 10px;
    box-shadow: var(--shadow-sm);
    min-width: 120px;
    z-index: 3;
    max-width: 180px;
}

.place-label {
    font-size: 10px;
    font-weight: 800;
    color: var(--slate-400);
    letter-spacing: 0.04em;
}

.place-name {
    font-size: 12px;
    font-weight: 800;
    color: var(--slate-800);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.place-pickup {
    left: 72px;
    top: 18px;
}

.place-dropoff {
    right: 14px;
    bottom: 18px;
}

.route-meta {
    background: #fff;
    padding: 12px 14px;
    border-top: 1px solid var(--slate-200);
}

.route-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 6px 0;
}

.dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-top: 6px;
}

.dot-green {
    background: var(--success);
}

.dot-red {
    background: var(--danger);
}

.route-item-title {
    font-size: 12px;
    font-weight: 800;
    color: var(--slate-800);
}

.route-item-sub {
    font-size: 12px;
    color: var(--slate-500);
    margin-top: 1px;
}

/* TIMELINE */
.timeline {
    padding-top: 4px;
}

.timeline-row {
    display: grid;
    grid-template-columns: 20px 1fr 72px;
    gap: 12px;
    align-items: stretch;
    padding: 8px 0;
}

.timeline-left {
    width: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.timeline-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-top: 4px;
}

.timeline-dot.done {
    background: var(--success);
}

.timeline-dot.pending {
    background: var(--slate-300);
}

.timeline-line {
    width: 2px;
    flex: 1;
    min-height: 22px;
    margin-top: 6px;
    border-radius: 999px;
    background: var(--slate-200);
}

.timeline-line.line-done {
    background: rgba(16, 185, 129, 0.55);
}

.timeline-line.line-pending {
    background: var(--slate-200);
}

.timeline-title {
    font-size: 13px;
    font-weight: 800;
    color: var(--slate-800);
}

.timeline-sub {
    font-size: 12px;
    color: var(--slate-500);
    margin-top: 1px;
}

.timeline-time {
    font-size: 12px;
    color: var(--slate-500);
    text-align: right;
    font-weight: 600;
    padding-top: 1px;
}

/* RIGHT CARDS */
.person-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.avatar {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 800;
    box-shadow: var(--shadow-sm);
    border: 1px solid transparent;
}

.avatar i {
    color: #fff;
    font-size: 15px;
    line-height: 1;
}

.avatar.blue {
    background: var(--info-text);
    border-color: var(--info-text);
}

.avatar.green {
    background: var(--success-text);
    border-color: var(--success-text);
}

.avatar.purple {
    background: var(--indigo);
    border-color: var(--indigo);
}

.person-name {
    font-size: 13px;
    font-weight: 800;
    color: var(--slate-800);
    line-height: 1.2;
}

.person-tag {
    font-size: 12px;
    color: var(--slate-500);
    margin-top: 2px;
    font-weight: 600;
}

.person-phone {
    font-size: 12px;
    color: var(--slate-700);
    font-weight: 600;
    margin: 6px 0 10px;
}

.stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}

.stat-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 12px;
    border: 1px solid var(--slate-200);
    background: #fff;
}

.stat-label {
    font-size: 12px;
    color: var(--slate-500);
    font-weight: 700;
}

.stat-value {
    font-size: 12px;
    color: var(--slate-800);
    font-weight: 800;
}

.rating .star {
    color: #f59e0b;
    margin-right: 6px;
}

.driver-status-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
}

.mini-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    text-transform: lowercase;
    line-height: 1;
}

.status-active {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.vehicle-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.vehicle-name {
    font-size: 13px;
    font-weight: 800;
    color: var(--slate-800);
}

.vehicle-plate {
    font-size: 12px;
    color: var(--slate-500);
    font-weight: 700;
    margin-top: 2px;
}

.kv-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 0;
}

.kv-key {
    font-size: 12px;
    color: var(--slate-500);
    font-weight: 700;
}

.kv-val {
    font-size: 12px;
    color: var(--slate-800);
    font-weight: 800;
}

.divider {
    height: 1px;
    background: var(--slate-200);
    margin: 8px 0;
}

.kv-row.total .kv-key {
    color: var(--slate-800);
}

.total-amount {
    color: var(--primary, #2563eb);
}

.paid-pill-wrap {
    margin-top: 8px;
}

.paid {
    background: rgba(16, 185, 129, 0.12);
    color: #059669;
}

.actions-wrap .action-btn {
    width: 100%;
    border-radius: 10px;
    font-weight: 800;
    font-size: 12px;
    padding: 10px 12px;
}
</style>
